<template>
  <div class="dashboard-wrap">
    <div class="dashboard-header">
      <div class="heading">Visit dashboard</div>
      <button class="btn-new-visit" @click="showNewVisitPanel = true">+ New Visit</button>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <div class="visits-toggle">
        <button class="toggle-opt" :class="{ active: myVisitsOnly }" @click="myVisitsOnly = true">My Visits</button>
        <button class="toggle-opt" :class="{ active: !myVisitsOnly }" @click="myVisitsOnly = false">All Visits</button>
      </div>
      <input v-model="filters.charId" class="filter-search" placeholder="Search Participant ID..." />
      <div class="filter-panel-wrap" ref="filterPanelWrap">
        <button class="btn-filter" :class="{ active: activeFilterCount > 0 }" @click="showFilterPanel = !showFilterPanel">
          <span>Filters</span>
          <span v-if="activeFilterCount" class="filter-badge">{{ activeFilterCount }}</span>
          <span class="filter-caret">{{ showFilterPanel ? '▲' : '▼' }}</span>
        </button>
        <div v-if="showFilterPanel" class="filter-dropdown">
          <div class="fp-section">
            <div class="fp-label">Status</div>
            <div class="fp-chips">
              <button v-for="st in statusOptions" :key="st.value"
                class="fp-chip" :class="{ active: statusFilter === st.value }"
                @click="statusFilter = st.value">{{ st.label }}</button>
            </div>
          </div>
          <div class="fp-section">
            <div class="fp-label">Date range</div>
            <div class="fp-date-row">
              <input type="date" v-model="filters.dateFrom" class="fp-input" />
              <span class="fp-to">to</span>
              <input type="date" v-model="filters.dateTo" class="fp-input" />
            </div>
          </div>
          <div class="fp-section">
            <div class="fp-label">Interviewer</div>
            <UserSearchSelect v-model="filters.interviewerId" :users="interviewerUsers" placeholder="Any interviewer..." />
          </div>
          <div class="fp-section">
            <div class="fp-label">QC</div>
            <UserSearchSelect v-model="filters.qcId" :users="qcUsers" placeholder="Any QC reviewer..." />
          </div>
          <div class="fp-footer">
            <button class="fp-clear" @click="clearFilters">Clear all</button>
            <button class="fp-done" @click="showFilterPanel = false">Done</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-state">Loading visits...</div>

    <!-- Session expired state -->
    <div v-else-if="sessionExpired" class="error-state">
      <p class="session-expired-title">Your session has expired</p>
      <p class="session-expired-sub">Please log in again to continue.</p>
      <button class="btn-login" @click="$router.push('/login')">Log In</button>
    </div>

    <!-- Error state -->
    <div v-else-if="loadError" class="error-state">
      <p>{{ loadError }}</p>
      <button class="btn-retry" @click="loadDashboard">Retry</button>
    </div>

    <!-- Browse layout -->
    <div v-else class="browse-layout">
      <div class="visit-list-col">
        <div v-if="loadingVisitDates" class="visit-dates-loading">
          <span class="loading-spinner"></span> Loading visit details...
        </div>
        <div class="visit-list">
          <div
            v-for="row in filteredRows"
            :key="row.participantDisplayId"
            class="visit-row"
            :class="{ active: selectedRow && selectedRow.participantDisplayId === row.participantDisplayId }"
            @click="selectRow(row)"
          >
            <div class="visit-row-top">
              <span class="visit-pid">{{ row.participantDisplayId }}</span>
              <span class="visit-status" :class="row.status">{{ row.statusLabel }}</span>
            </div>
            <div class="visit-row-meta-line">
              <span class="visit-meta-label">Date</span>
              <span class="visit-meta-value">{{ row.date || '—' }}</span>
            </div>
            <div class="visit-row-meta-line" v-if="interviewerNameForRow(row)">
              <span class="visit-meta-label">Int</span>
              <span class="visit-meta-value">{{ interviewerNameForRow(row) }}</span>
            </div>
            <div class="visit-row-meta-line" v-if="qcNameForRow(row)">
              <span class="visit-meta-label">QC</span>
              <span class="visit-meta-value">{{ qcNameForRow(row) }}</span>
            </div>
          </div>
          <div v-if="filteredRows.length === 0" class="visit-list-empty">
            No visits match the current filters.
          </div>
        </div>
      </div>

      <!-- Detail panel -->
      <div class="visit-detail" v-if="selectedRow">
        <!-- Header -->
        <div class="detail-header">
          <div class="detail-title">{{ selectedRow.participantDisplayId }}</div>
          <div class="detail-header-actions">
            <button class="btn-export-csv" @click="exportVisitCsv" title="Export visit info as CSV">↓ Export Visit Info</button>
            <span class="visit-status" :class="selectedRow.status">{{ selectedRow.statusLabel }}</span>
          </div>
        </div>

        <!-- Prominent editable fields -->
        <div class="detail-fields">
          <div class="detail-field">
            <span class="detail-field-label">Date</span>
            <div class="detail-field-value" v-if="!editingDate">
              <span :class="{ 'field-empty': !(selectedVisitData && selectedVisitData.date) }">
                {{ selectedVisitData && selectedVisitData.date ? selectedVisitData.date : 'Not set' }}
              </span>
              <button class="edit-icon-btn" @click="startEditDate" title="Edit date">✏️</button>
            </div>
            <div class="detail-field-edit" v-else>
              <input type="date" v-model="editDate" class="field-input" />
              <button class="save-btn" @click="saveDate" :disabled="savingDate">✓</button>
              <button class="cancel-btn" @click="editingDate = false">✕</button>
            </div>
          </div>

          <div class="detail-field">
            <span class="detail-field-label">Interviewer</span>
            <div class="detail-field-value" v-if="!editingInterviewer">
              <span :class="{ 'field-empty': !selectedInterviewerName }">
                {{ selectedInterviewerName || 'Not assigned' }}
              </span>
              <button class="edit-icon-btn" @click="startEditInterviewer" title="Edit interviewer">✏️</button>
            </div>
            <div class="detail-field-edit" v-else>
              <UserSearchSelect
                v-model="editInterviewerUserId"
                :users="interviewerUsers"
                placeholder="Search interviewer..."
                class="field-input"
              />
              <button class="save-btn" @click="saveInterviewer" :disabled="savingInterviewer || !editInterviewerUserId">✓</button>
              <button class="cancel-btn" @click="editingInterviewer = false">✕</button>
            </div>
          </div>

          <div class="detail-field">
            <span class="detail-field-label">QC</span>
            <div class="detail-field-value" v-if="!editingQc">
              <span :class="{ 'field-empty': !selectedQcName }">
                {{ selectedQcName || 'Not assigned' }}
              </span>
              <button class="edit-icon-btn" @click="startEditQc" title="Assign QC">✏️</button>
            </div>
            <div class="detail-field-edit" v-else>
              <UserSearchSelect
                v-model="editQcUserId"
                :users="qcUsers"
                placeholder="Search QC reviewer..."
                class="field-input"
              />
              <button class="save-btn" @click="saveQc" :disabled="savingQc || !editQcUserId">✓</button>
              <button class="cancel-btn" @click="editingQc = false">✕</button>
            </div>
          </div>

          <div class="detail-field">
            <span class="detail-field-label">Status</span>
            <div class="detail-field-value" v-if="!editingStatus">
              <span class="visit-status" :class="selectedRow.status">{{ selectedRow.statusLabel }}</span>
              <button class="edit-icon-btn" @click="startEditStatus" title="Change status">✏️</button>
            </div>
            <div class="detail-field-edit" v-else>
              <select v-model="editStatus" class="field-input">
                <option value="pending">Pending</option>
                <option value="qc-needed">QC Needed</option>
                <option value="change-requested">Change Requested</option>
                <option value="complete">Complete</option>
              </select>
              <button class="save-btn" @click="saveStatus" :disabled="savingStatus">✓</button>
              <button class="cancel-btn" @click="editingStatus = false">✕</button>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="tabs">
          <button class="tab-btn" :class="{ active: activeTab === 'administer' }" @click="activeTab = 'administer'">Administer</button>
          <button class="tab-btn" @click="openScoringSheet">Score</button>
        </div>

        <!-- Administer tab -->
        <div v-if="activeTab === 'administer'" class="tab-panel administer-panel">
          <p class="administer-hint">Ready to begin the session with <strong>{{ selectedRow.participantDisplayId }}</strong>?</p>
          <button class="btn-start-admin" @click="startAdministering">▶ Start Administering</button>
        </div>
      </div>

      <div class="visit-detail empty-detail" v-else>
        <p>Select a visit to see details.</p>
      </div>
    </div>

    <NewVisitPanel
      :visible="showNewVisitPanel"
      :rows="rows"
      :l-group-id="lGroupId"
      @close="showNewVisitPanel = false"
      @visit-created="onVisitCreated"
    />

    <ScoringPanel
      v-if="showScoringPanel"
      :isOpen="showScoringPanel"
      @close="showScoringPanel = false"
    />
  </div>
</template>

<script>
import ACTApiService from '@/services/ACTApiService';
import NewVisitPanel from '@/components/NewVisitPanel.vue';
import UserSearchSelect from '@/components/UserSearchSelect.vue';
import ScoringPanel from '@/components/ScoringPanel.vue';
import ServerCodeModel from '@/models/ServerCodeModel';
import CASIController from '@/controllers/CASIController';

const api = new ACTApiService();

const VISIT_TYPE_LABELS = {
  biennial: 'Biennial'
};


export default {
  name: 'VisitDashboard',
  components: { NewVisitPanel, UserSearchSelect, ScoringPanel },
  data() {
    return {
      loading: false,
      loadError: null,
      sessionExpired: false,
      lGroupId: null,
      visitTypeId: null,
      participants: [],
      myWorkflowTasks: [],
      interviewerUsers: [],
      qcUsers: [],
      loadingUsers: false,
      selectedVisitData: null,

      statusFilter: 'all',
      myVisitsOnly: true,
      filters: { charId: '', dateFrom: '', dateTo: '', interviewerId: '', qcId: '' },
      showFilterPanel: false,
      statusOptions: [
        { value: 'all', label: 'All' },
        { value: 'needs-assignment', label: 'Needs Assignment' },
        { value: 'pending', label: 'Pending' },
        { value: 'qc-needed', label: 'QC Needed' },
        { value: 'change-requested', label: 'Change Requested' },
        { value: 'complete', label: 'Complete' }
      ],

      selectedRow: null,
      activeTab: 'administer',
      loadingDetail: false,

      editingDate: false,
      editDate: '',
      savingDate: false,
      editingInterviewer: false,
      editInterviewerUserId: '',
      savingInterviewer: false,

      editingQc: false,
      editQcUserId: '',
      savingQc: false,

      editingStatus: false,
      editStatus: '',
      savingStatus: false,

      showNewVisitPanel: false,
      showScoringPanel: false,
      loadingVisitDates: false,

      // rows built from participants + workflow tasks
      rows: []
    };
  },
  computed: {
    myParticipantIds() {
      return new Set(this.myWorkflowTasks.map(t => t.ParticipantId || t.participantId));
    },
    activeFilterCount() {
      let n = 0;
      if (this.statusFilter !== 'all') n++;
      if (this.filters.dateFrom || this.filters.dateTo) n++;
      if (this.filters.interviewerId) n++;
      if (this.filters.qcId) n++;
      return n;
    },
    selectedInterviewerName() {
      if (!this.selectedRow) return null;
      return this.interviewerNameForRow(this.selectedRow);
    },
    selectedQcName() {
      if (!this.selectedRow) return null;
      return this.qcNameForRow(this.selectedRow);
    },
    filteredRows() {
      let list = this.rows.slice();

      if (this.myVisitsOnly) {
        list = list.filter(r => this.myParticipantIds.has(r.participantDisplayId));
      }
      if (this.statusFilter !== 'all') {
        list = list.filter(r => r.status === this.statusFilter);
      }
      if (this.filters.charId.trim()) {
        const q = this.filters.charId.trim().toLowerCase();
        list = list.filter(r => r.participantDisplayId.toLowerCase().includes(q));
      }
      if (this.filters.dateFrom) {
        list = list.filter(r => r.date && r.date >= this.filters.dateFrom);
      }
      if (this.filters.dateTo) {
        list = list.filter(r => r.date && r.date <= this.filters.dateTo);
      }
      if (this.filters.interviewerId) {
        list = list.filter(r => {
          const task = this.myWorkflowTasks.find(t =>
            (t.ParticipantId || t.participantId) === r.participantDisplayId &&
            (t.TaskType || t.taskType) === 'Interview'
          );
          return task && (task.TargetPortalUserId || task.targetPortalUserId) == this.filters.interviewerId;
        });
      }
      if (this.filters.qcId) {
        list = list.filter(r => {
          const task = this.myWorkflowTasks.find(t =>
            (t.ParticipantId || t.participantId) === r.participantDisplayId &&
            (t.TaskType || t.taskType) === 'QC'
          );
          return task && (task.TargetPortalUserId || task.targetPortalUserId) == this.filters.qcId;
        });
      }

      return list.sort((a, b) => a.participantDisplayId.localeCompare(b.participantDisplayId));
    }
  },
  async created() {
    await this.loadDashboard();
  },
  mounted() {
    document.addEventListener('mousedown', this.onFilterOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onFilterOutsideClick);
  },
  methods: {
    deriveStatus(pid, visitDataStatus) {
      if (visitDataStatus === 'complete') return { status: 'complete', statusLabel: 'Complete' };
      if (visitDataStatus === 'change-requested') return { status: 'change-requested', statusLabel: 'Change Requested' };
      if (visitDataStatus === 'qc-needed') return { status: 'qc-needed', statusLabel: 'QC Needed' };
      const hasInterviewer = this.myWorkflowTasks.some(t =>
        (t.ParticipantId || t.participantId) === pid && (t.TaskType || t.taskType) === 'Interview'
      );
      return hasInterviewer
        ? { status: 'pending', statusLabel: 'Pending' }
        : { status: 'needs-assignment', statusLabel: 'Needs Assignment' };
    },
    onFilterOutsideClick(e) {
      if (this.showFilterPanel && !this.$refs.filterPanelWrap?.contains(e.target)) {
        this.showFilterPanel = false;
      }
    },
    clearFilters() {
      this.statusFilter = 'all';
      this.filters.dateFrom = '';
      this.filters.dateTo = '';
      this.filters.interviewerId = '';
      this.filters.qcId = '';
    },
    async loadDashboard() {
      this.loading = true;
      this.loadError = null;
      this.sessionExpired = false;
      try {
        this.lGroupId = await api.getLGroups();
        this.visitTypeId = await api.getVisitTypes(this.lGroupId);

        const [participants, myTasks, interviewerUsers, qcUsers] = await Promise.allSettled([
          api.getParticipants(this.lGroupId),
          api.getMyWorkflowTasks(),
          api.getPortalUsersByRole('ACTInterview', 'Interviewer'),
          api.getPortalUsersByRole('ACTInterview', 'QC'),
        ]);

        this.participants = participants.status === 'fulfilled' ? participants.value : [];
        this.myWorkflowTasks = myTasks.status === 'fulfilled' ? myTasks.value : [];
        this.interviewerUsers = interviewerUsers.status === 'fulfilled' ? interviewerUsers.value : [];
        this.qcUsers = qcUsers.status === 'fulfilled' ? qcUsers.value : [];

        this.buildRows();
        this.loadAllVisitDates();
      } catch (e) {
        if (e.message?.includes('401') || e.message?.includes('Unauthorized')) {
          this.sessionExpired = true;
        } else {
          this.loadError = e.message || 'Failed to load dashboard.';
        }
      } finally {
        this.loading = false;
      }
    },

    async loadAllVisitDates() {
      const vtId = this.visitTypeId || localStorage.getItem('biennialVisitTypeId');
      if (!vtId || !this.rows.length) return;

      this.loadingVisitDates = true;
      const BATCH = 10;
      try {
        for (let i = 0; i < this.rows.length; i += BATCH) {
          const batch = this.rows.slice(i, i + BATCH);
          const results = await Promise.allSettled(
            batch.map(row => api.getVisit(row.participantDisplayId, vtId, false))
          );
          results.forEach((result, j) => {
            if (result.status === 'fulfilled' && result.value?.success) {
              const raw = result.value.data?.VisitData || result.value.data?.visitData || result.value.data;
              if (!raw) return;
              const pid = batch[j].participantDisplayId;
              const idx = this.rows.findIndex(r => r.participantDisplayId === pid);
              if (idx >= 0) {
                const updates = {};
                const date = raw.date || raw.Date;
                if (date) updates.date = date;
                const interviewerPortalUserId = raw.interviewerPortalUserId || raw.InterviewerPortalUserId;
                const qcPortalUserId = raw.qcPortalUserId || raw.QcPortalUserId;
                if (interviewerPortalUserId) updates.interviewerPortalUserId = interviewerPortalUserId;
                if (qcPortalUserId) updates.qcPortalUserId = qcPortalUserId;
                const savedStatus = raw.status || raw.Status;
                Object.assign(updates, this.deriveStatus(pid, savedStatus || null));
                this.rows[idx] = { ...this.rows[idx], ...updates };
              }
            }
          });
        }
      } finally {
        this.loadingVisitDates = false;
      }
    },

    buildRows() {
      const statusCache = JSON.parse(localStorage.getItem('visitStatusCache') || '{}');
      const taskMap = {};
      for (const t of this.myWorkflowTasks) {
        const pid = t.ParticipantId || t.participantId;
        if (pid && !taskMap[pid]) taskMap[pid] = t;
      }

      // Start with lGroup participants that match ACTB####
      const seen = new Set();
      const rows = [];

      for (const p of this.participants) {
        const displayId = p.ParticipantDisplayId || p.participantDisplayId || p.ParticipantId;
        if (!displayId || !/^ACTB\d+$/.test(displayId) || seen.has(displayId)) continue;
        seen.add(displayId);
        const task = taskMap[displayId] || null;
        rows.push({
          participantDisplayId: displayId,
          participantId: p.ParticipantId || p.participantId,
          visitTypeId: 'biennial',
          visitId: task ? (task.VisitId || task.visitId) : null,
          date: null,
          ...this.deriveStatus(displayId, statusCache[displayId] || null)
        });
      }

      // Add any ACTB#### participants from workflow tasks not already in the list
      for (const t of this.myWorkflowTasks) {
        const pid = t.ParticipantId || t.participantId;
        if (!pid || !/^ACTB\d+$/.test(pid) || seen.has(pid)) continue;
        seen.add(pid);
        rows.push({
          participantDisplayId: pid,
          participantId: pid,
          visitTypeId: 'biennial',
          visitId: t.VisitId || t.visitId,
          date: null,
          ...this.deriveStatus(pid, statusCache[pid] || null)
        });
      }

      this.rows = rows;
    },

    visitTypeLabel(id) {
      return VISIT_TYPE_LABELS[id] || id || '—';
    },

    startEditDate() {
      this.editDate = this.selectedVisitData?.date || '';
      this.editingDate = true;
    },
    async saveDate() {
      this.savingDate = true;
      try {
        const pid = this.selectedRow.participantDisplayId;
        const vtId = this.visitTypeId || localStorage.getItem('biennialVisitTypeId');
        const newVisitData = { ...(this.selectedVisitData || {}), date: this.editDate };
        await api.createVisit(this.lGroupId, pid, vtId, pid, newVisitData);
        this.selectedVisitData = newVisitData;
        // also update left-column date
        const idx = this.rows.findIndex(r => r.participantDisplayId === pid);
        if (idx >= 0) this.rows[idx] = { ...this.rows[idx], date: this.editDate };
        this.editingDate = false;
      } catch (e) {
        console.error('Failed to save date:', e);
      } finally {
        this.savingDate = false;
      }
    },
    startEditInterviewer() {
      this.editInterviewerUserId = '';
      this.editingInterviewer = true;
    },
    async saveInterviewer() {
      this.savingInterviewer = true;
      try {
        const pid = this.selectedRow.participantDisplayId;
        const vtId = this.visitTypeId || localStorage.getItem('biennialVisitTypeId');
        const visitId = this.selectedRow.visitId || '';
        await api.createWorkflowTask('Interview', pid, visitId, vtId, this.editInterviewerUserId);
        // Also persist in visit data so other users/sessions can see it
        const newVisitData = { ...(this.selectedVisitData || {}), interviewerPortalUserId: this.editInterviewerUserId };
        await api.createVisit(this.lGroupId, pid, vtId, pid, newVisitData);
        this.selectedVisitData = newVisitData;
        // Replace local task so computed re-evaluates
        this.myWorkflowTasks = [
          ...this.myWorkflowTasks.filter(t =>
            !((t.ParticipantId || t.participantId) === pid && (t.TaskType || t.taskType) === 'Interview')
          ),
          { participantId: pid, visitId, visitTypeId: vtId, targetPortalUserId: this.editInterviewerUserId, taskType: 'Interview' }
        ];
        // Re-derive status and update row with new interviewerPortalUserId
        const savedStatus = this.selectedVisitData?.status || null;
        const derived = this.deriveStatus(pid, savedStatus);
        const ridx = this.rows.findIndex(r => r.participantDisplayId === pid);
        if (ridx >= 0) this.rows[ridx] = { ...this.rows[ridx], ...derived, interviewerPortalUserId: this.editInterviewerUserId };
        this.selectedRow = { ...this.selectedRow, ...derived, interviewerPortalUserId: this.editInterviewerUserId };
        this.editingInterviewer = false;
      } catch (e) {
        console.error('Failed to save interviewer:', e);
      } finally {
        this.savingInterviewer = false;
      }
    },
    async exportVisitCsv() {
      const row = this.selectedRow;
      const vd = this.selectedVisitData || {};
      const pid = row.participantDisplayId;

      const interviewerTask = this.myWorkflowTasks.find(t =>
        (t.ParticipantId || t.participantId) === pid && (t.TaskType || t.taskType) === 'Interview'
      );
      const qcTask = this.myWorkflowTasks.find(t =>
        (t.ParticipantId || t.participantId) === pid && (t.TaskType || t.taskType) === 'QC'
      );
      const interviewerId = interviewerTask
        ? (interviewerTask.TargetPortalUserId || interviewerTask.targetPortalUserId)
        : (vd.interviewerPortalUserId || '');
      const qcId = qcTask
        ? (qcTask.TargetPortalUserId || qcTask.targetPortalUserId)
        : (vd.qcPortalUserId || '');

      // Check if participant has saved CASI data
      const subjectData = JSON.parse(localStorage.getItem('subjectData') || '{}');
      const participantData = subjectData[pid] || {};
      const hasCasi = participantData['casiData'] &&
        Array.isArray(participantData['casiData'].sections) &&
        participantData['casiData'].sections.length > 0;

      let s = {};
      let ctrl = null;
      if (hasCasi) {
        const prevId = localStorage.getItem('subjectId');
        localStorage.setItem('subjectId', pid);
        ctrl = new CASIController();
        await ctrl.loadData();
        ctrl.calculateScores();
        localStorage.setItem('subjectId', prevId || '');
        s = ctrl.rawScores || {};
      }

      const v = (val) => (val !== undefined && val !== null && val !== '') ? val : '';

      const fields = [
        `"${pid}"`,                          // subjectvisit
        pid,                                 // charsub
        v(vd.date),                          // date
        v(vd.visitNumber),                   // visit
        2,                                   // vtype
        v(interviewerId),                    // int
        vd.date ? 2 : 0,                     // visit_information_complete
        v(qcId),                             // qc
        '',                                  // visitname
        '',                                  // fname
        v(interviewerId),                    // interviewer
        v(row.participantId),                // subjectid
        v(vd.visitNumber),                   // visitnum
        '',                                  // visitype
        pid,                                 // initials
        '',                                  // baselinedt
        '',                                  // intakedt
        '',                                  // fdate
        '',                                  // casi_intake
        '',                                  // lastcasi
        '',                                  // thiscasi
        '',                                  // alfimmse
        '',                                  // shortcasi
        '',                                  // jormsneed
        '',                                  // jormsscore
        '',                                  // evalneed
        '',                                  // evalreason
        '',                                  // diagdone
        '',                                  // noevalreason
        '',                                  // incohort
        '',                                  // mailed
        '',                                  // maildate
        '',                                  // remotereason
        '',                                  // telephonereason
        '',                                  // covercomments
        0,                                   // cover_sheet_complete
        0,                                   // form_51
        '', '', '', '',                      // tr_a_tm, tr_a_err, tr_b_tm, tr_b_err
        '', '', '', '', '', '',              // w_lm_ima/b/t dea/b/t
        '', '', '', '', '', '', '',          // clockdr, sup_cor, let_cor, sup_inc, let_inc, sup_rep, let_rep
        0,                                   // form_51_psych_0_2_complete
        0,                                   // form_84
        '', '', '', '',                      // casi1-4
        '', '',                              // start_hr, start_mn
        hasCasi ? ctrl.totalScore : '',      // casi_sc
        v(s.BPL), v(s.BYR), v(s.BDAY), v(s.AGE),
        v(s.MNT), v(s.SUN),
        '',                                  // vrs
        v(s.RGS1), v(s.RGS2),
        v(s.DBA), v(s.DBB), v(s.DBC),
        v(s.RC1A), v(s.RC1B), v(s.RC1C),
        v(s.SUB3A), v(s.SUB3B), v(s.SUB3C),
        v(s.YR), v(s.MO), v(s.DATE), v(s.DAY), v(s.SSN),
        v(s.SPA), v(s.SPB),
        v(s.ANML),                           // animal
        v(s.SIM), v(s.JGMT),
        v(s.RPTA), v(s.RPTB),
        v(s.READ), v(s.WRITE),               // cas_read, cas_writ
        v(s.DRAW), v(s.CMD),
        v(s.RC2A), v(s.RC2B), v(s.RC2C),
        v(s.BODY), '', '',                   // body, obja, objb
        '', v(s.RCOBJ),                      // rpnm, rcobj
        '', '', '', '',                      // valid, casi_sta, fin_hr, fin_min
        '',                                  // durat
        hasCasi ? ctrl.casiSTM : '',
        hasCasi ? ctrl.casiLANG : '',
        hasCasi ? ctrl.casiLTM : '',
        hasCasi ? ctrl.casiATT : '',
        hasCasi ? ctrl.casiMMC : '',
        hasCasi ? ctrl.casiORI : '',
        hasCasi ? ctrl.casiATAJ : '',
        hasCasi ? ctrl.totalScore : '',      // calc_casi
        hasCasi ? 2 : 0                      // form_84_casi_0_2_complete
      ];

      const headers = 'subjectvisit,charsub,date,visit,vtype,int,visit_information_complete,qc,visitname,fname,interviewer,subjectid,visitnum,visitype,initials,baselinedt,intakedt,fdate,casi_intake,lastcasi,thiscasi,alfimmse,shortcasi,jormsneed,jormsscore,evalneed,evalreason,diagdone,noevalreason,incohort,mailed,maildate,remotereason,telephonereason,covercomments,cover_sheet_complete,form_51,tr_a_tm,tr_a_err,tr_b_tm,tr_b_err,w_lm_ima,w_lm_dea,w_lm_imb,w_lm_deb,w_lm_imt,w_lm_det,clockdr,sup_cor,let_cor,sup_inc,let_inc,sup_rep,let_rep,form_51_psych_0_2_complete,form_84,casi1,casi2,casi3,casi4,start_hr,start_mn,casi_sc,bpl,byr,bday,age,mnt,sun,vrs,rgs1,rgs2,dba,dbb,dbc,rc1a,rc1b,rc1c,sub3a,sub3b,sub3c,yr,mo,casi_dat,day,casi_ssn,spa,spb,animal,sim,jgmt,rpta,rptb,cas_read,cas_writ,draw,cmd,rc2a,rc2b,rc2c,body,obja,objb,rpnm,rcobj,valid,casi_sta,fin_hr,fin_min,durat,stm,lang,ltm,atten,menma,orien,absjgmt,calc_casi,form_84_casi_0_2_complete';

      const csv = `${headers}\n${fields.join(',')}`;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pid}_visit_info.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    openScoringSheet() {
      if (!this.selectedRow) return;
      localStorage.setItem('subjectId', this.selectedRow.participantDisplayId);
      localStorage.setItem('currentSubjectId', this.selectedRow.participantDisplayId);
      localStorage.setItem('currentParticipantId', this.selectedRow.participantId || this.selectedRow.participantDisplayId);
      if (this.selectedRow.visitId) localStorage.setItem('visitId', this.selectedRow.visitId);
      this.showScoringPanel = true;
    },
    async startAdministering() {
      const row = this.selectedRow;
      const subjectId = row.participantDisplayId;
      localStorage.setItem('subjectId', subjectId);
      localStorage.setItem('currentSubjectId', subjectId);
      localStorage.setItem('currentParticipantId', row.participantId || subjectId);
      localStorage.setItem('visitTypeId', this.visitTypeId || '');
      const serverCodeModel = new ServerCodeModel();
      await serverCodeModel.loadForAdminister(subjectId);
      this.$router.push('/introduction');
    },
    async selectRow(row) {
      const pid = row.participantDisplayId;
      this.selectedRow = row;
      this.activeTab = 'administer';
      this.selectedVisitData = null;
      this.editingDate = false;
      this.editingInterviewer = false;
      this.editingQc = false;
      this.editingStatus = false;
      const vtId = this.visitTypeId || localStorage.getItem('biennialVisitTypeId');
      if (vtId) {
        const result = await api.getVisit(pid, vtId);
        // Guard: skip if a different row was selected while awaiting, or user already saved data
        if (this.selectedRow?.participantDisplayId !== pid) return;
        if (result?.success && this.selectedVisitData === null) {
          const raw = result.data?.VisitData || result.data?.visitData || result.data || null;
          if (raw) {
            this.selectedVisitData = {
              ...raw,
              date: raw.date || raw.Date || '',
              visitNumber: raw.visitNumber || raw.VisitNumber || '',
              interviewerPortalUserId: raw.interviewerPortalUserId || raw.InterviewerPortalUserId || '',
              qcPortalUserId: raw.qcPortalUserId || raw.QcPortalUserId || ''
            };
          }
          const idx = this.rows.findIndex(r => r.participantDisplayId === pid);
          if (idx >= 0) {
            const updates = {};
            if (this.selectedVisitData?.date) updates.date = this.selectedVisitData.date;
            if (this.selectedVisitData?.interviewerPortalUserId) updates.interviewerPortalUserId = this.selectedVisitData.interviewerPortalUserId;
            if (this.selectedVisitData?.qcPortalUserId) updates.qcPortalUserId = this.selectedVisitData.qcPortalUserId;
            const savedStatus = raw?.status || raw?.Status;
            Object.assign(updates, this.deriveStatus(pid, savedStatus || null));
            this.rows[idx] = { ...this.rows[idx], ...updates };
            this.selectedRow = this.rows[idx];
          }
        }
      }
    },

    startEditStatus() {
      const cur = this.selectedRow?.status;
      this.editStatus = (['change-requested', 'complete', 'qc-needed'].includes(cur)) ? cur : 'pending';
      this.editingStatus = true;
    },
    async saveStatus() {
      this.savingStatus = true;
      try {
        const pid = this.selectedRow.participantDisplayId;
        const vtId = this.visitTypeId || localStorage.getItem('biennialVisitTypeId');
        const savedStatus = this.editStatus === 'pending' ? null : this.editStatus;
        const newVisitData = { ...(this.selectedVisitData || {}), status: savedStatus };
        await api.createVisit(this.lGroupId, pid, vtId, pid, newVisitData);
        // Cache status locally so it survives reload for participants without workflow tasks
        const cache = JSON.parse(localStorage.getItem('visitStatusCache') || '{}');
        if (savedStatus) cache[pid] = savedStatus; else delete cache[pid];
        localStorage.setItem('visitStatusCache', JSON.stringify(cache));
        this.selectedVisitData = newVisitData;
        const derived = this.deriveStatus(pid, savedStatus);
        const idx = this.rows.findIndex(r => r.participantDisplayId === pid);
        if (idx >= 0) this.rows[idx] = { ...this.rows[idx], ...derived };
        this.selectedRow = { ...this.selectedRow, ...derived };
        this.editingStatus = false;
      } catch (e) {
        console.error('Failed to save status:', e);
      } finally {
        this.savingStatus = false;
      }
    },
    startEditQc() {
      this.editQcUserId = '';
      this.editingQc = true;
    },
    async saveQc() {
      this.savingQc = true;
      try {
        const pid = this.selectedRow.participantDisplayId;
        const vtId = this.visitTypeId || localStorage.getItem('biennialVisitTypeId');
        const visitId = this.selectedRow.visitId || '';
        await api.createWorkflowTask('QC', pid, visitId, vtId, this.editQcUserId);
        // Also persist in visit data so other users/sessions can see it
        const newVisitData = { ...(this.selectedVisitData || {}), qcPortalUserId: this.editQcUserId };
        await api.createVisit(this.lGroupId, pid, vtId, pid, newVisitData);
        this.selectedVisitData = newVisitData;
        this.myWorkflowTasks = [
          ...this.myWorkflowTasks.filter(t =>
            !((t.ParticipantId || t.participantId) === pid && (t.TaskType || t.taskType) === 'QC')
          ),
          { participantId: pid, visitId, visitTypeId: vtId, targetPortalUserId: this.editQcUserId, taskType: 'QC' }
        ];
        const ridx = this.rows.findIndex(r => r.participantDisplayId === pid);
        if (ridx >= 0) this.rows[ridx] = { ...this.rows[ridx], qcPortalUserId: this.editQcUserId };
        this.selectedRow = { ...this.selectedRow, qcPortalUserId: this.editQcUserId };
        this.editingQc = false;
      } catch (e) {
        console.error('Failed to assign QC:', e);
      } finally {
        this.savingQc = false;
      }
    },
    interviewerNameForRow(row) {
      const task = this.myWorkflowTasks.find(t =>
        (t.ParticipantId || t.participantId) === row.participantDisplayId &&
        (t.TaskType || t.taskType) === 'Interview'
      );
      const uid = task
        ? (task.TargetPortalUserId || task.targetPortalUserId)
        : row.interviewerPortalUserId;
      if (!uid) return null;
      const user = this.interviewerUsers.find(u =>
        (u.PortalUserId || u.portalUserId || u.PrimaryKey || u.primaryKey) == uid
      );
      return user ? (user.Username || user.username || user.EmailAddress || user.emailAddress) : null;
    },
    qcNameForRow(row) {
      const task = this.myWorkflowTasks.find(t =>
        (t.ParticipantId || t.participantId) === row.participantDisplayId &&
        (t.TaskType || t.taskType) === 'QC'
      );
      const uid = task
        ? (task.TargetPortalUserId || task.targetPortalUserId)
        : row.qcPortalUserId;
      if (!uid) return null;
      const user = this.qcUsers.find(u =>
        (u.PortalUserId || u.portalUserId || u.PrimaryKey || u.primaryKey) == uid
      );
      return user ? (user.Username || user.username || user.EmailAddress || user.emailAddress) : null;
    },

    async onVisitCreated(payload) {
      this.showNewVisitPanel = false;
      console.log('[Dashboard] onVisitCreated payload:', payload);
      await this.loadDashboard();
      console.log('[Dashboard] After reload — myParticipantIds:', [...this.myParticipantIds], '| total rows:', this.rows.length);
      let newRow = this.rows.find(r => r.participantDisplayId === payload.charId);
      console.log('[Dashboard] newRow found after reload:', newRow);

      if (!newRow) {
        // createVisit saves data but does not register the participant in the lGroup participant
        // list, so getParticipants won't return it. If the task is also assigned to another user,
        // getMyWorkflowTasks won't return it either. Manually insert the row so it's visible.
        console.log('[Dashboard] Row missing after reload — manually inserting row for', payload.charId);
        const statusInfo = payload.interviewerPortalUserId
          ? { status: 'pending', statusLabel: 'Pending' }
          : { status: 'needs-assignment', statusLabel: 'Needs Assignment' };
        newRow = {
          participantDisplayId: payload.charId,
          participantId: payload.charId,
          visitTypeId: 'biennial',
          visitId: payload.visitId || payload.charId,
          date: payload.date || null,
          interviewerPortalUserId: payload.interviewerPortalUserId || null,
          ...statusInfo
        };
        this.rows = [...this.rows, newRow];
      }

      if (!this.myParticipantIds.has(payload.charId)) {
        console.log('[Dashboard] New visit not in My Visits (assigned to another user), switching to All Visits');
        this.myVisitsOnly = false;
      }
      this.selectedRow = newRow;
      this.activeTab = 'administer';
    }
  }
};
</script>

<style scoped>
.dashboard-wrap {
  max-width: 1100px;
  margin: 30px auto;
  padding: 0 20px;
}

.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.dashboard-header .heading {
  font-size: 24px;
  font-weight: 600;
  color: #237091;
}

.btn-new-visit {
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 600;
  background: #3986A8;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
.btn-new-visit:hover { background: #237091; }

.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.visits-toggle {
  display: flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  flex-shrink: 0;
}
.toggle-opt {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.toggle-opt:hover { color: #237091; }
.toggle-opt.active { background: white; color: #237091; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }

.filter-search {
  flex: 1;
  padding: 7px 12px;
  font-size: 13px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #111;
  outline: none;
  min-width: 0;
}
.filter-search:focus { border-color: #3986A8; }
.filter-search::placeholder { color: #b0b7c3; }

.filter-panel-wrap {
  position: relative;
  flex-shrink: 0;
}

.btn-filter {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #555;
  cursor: pointer;
  white-space: nowrap;
}
.btn-filter:hover { border-color: #3986A8; color: #237091; }
.btn-filter.active { border-color: #3986A8; background: #e8f4f8; color: #237091; }
.filter-badge {
  background: #3986A8;
  color: white;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  min-width: 18px;
  text-align: center;
}
.filter-caret { font-size: 10px; color: #9ca3af; }

.filter-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 320px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 300;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.fp-section { display: flex; flex-direction: column; gap: 8px; }
.fp-label {
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.fp-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.fp-chip {
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  background: white;
  color: #555;
  cursor: pointer;
}
.fp-chip:hover { border-color: #3986A8; color: #237091; }
.fp-chip.active { background: #3986A8; border-color: #3986A8; color: white; }

.fp-date-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.fp-input {
  flex: 1;
  padding: 7px 10px;
  font-size: 13px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  outline: none;
  min-width: 0;
}
.fp-input:focus { border-color: #3986A8; }
.fp-to { font-size: 12px; color: #9ca3af; flex-shrink: 0; }

.fp-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 4px;
  border-top: 1px solid #f3f4f6;
}
.fp-clear {
  font-size: 13px;
  color: #9ca3af;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 0;
}
.fp-clear:hover { color: #374151; }
.fp-done {
  padding: 7px 20px;
  font-size: 13px;
  font-weight: 600;
  background: #3986A8;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
.fp-done:hover { background: #237091; }

.loading-state,
.error-state {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  font-size: 15px;
}

.session-expired-icon { font-size: 36px; margin-bottom: 8px; }
.session-expired-title { font-size: 17px; font-weight: 600; color: #374151; margin: 0 0 6px; }
.session-expired-sub { font-size: 14px; color: #6b7280; margin: 0 0 20px; }
.btn-login {
  padding: 10px 28px;
  background: #3986A8;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}
.btn-login:hover { background: #237091; }

.btn-retry {
  margin-top: 12px;
  padding: 8px 20px;
  background: #3986A8;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}

.browse-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 16px;
  align-items: start;
}

.visit-list-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
}


.visit-dates-loading {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #6b7280;
  padding: 6px 2px;
}

.loading-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid #d1d5db;
  border-top-color: #3986A8;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.visit-list {
  border: 1px solid #a0a0a0;
  border-radius: 6px;
  overflow: hidden;
  background: #F4F4F4;
}

.visit-row {
  padding: 12px 14px;
  border-bottom: 1px solid #d1d5db;
  cursor: pointer;
  transition: background 0.12s;
}
.visit-row:last-child { border-bottom: none; }
.visit-row:hover { background: #e8f4f8; }
.visit-row.active { background: #d0eaf4; border-left: 3px solid #3986A8; }

.visit-row-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; }
.visit-pid { font-weight: bold; font-size: 14px; color: #237091; }
.visit-row-meta-line {
  display: flex;
  align-items: baseline;
  gap: 5px;
  margin-top: 2px;
}
.visit-meta-label {
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  width: 24px;
  flex-shrink: 0;
}
.visit-meta-value {
  font-size: 12px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.visit-status {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}
.visit-status.complete          { background: #dcfce7; color: #166534; }
.visit-status.pending           { background: #dbeafe; color: #1d4ed8; }
.visit-status.needs-assignment  { background: #fef3c7; color: #92400e; }
.visit-status.qc-needed         { background: #ede9fe; color: #5b21b6; }
.visit-status.change-requested  { background: #fce7f3; color: #9d174d; }

.visit-list-empty {
  padding: 24px 14px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.visit-detail {
  border: 1px solid #a0a0a0;
  border-radius: 6px;
  background: #F4F4F4;
  overflow: hidden;
}
.visit-detail.empty-detail {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #aaa;
  font-size: 14px;
}

.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 18px 12px;
  border-bottom: 1px solid #d1d5db;
  background: white;
}
.detail-title { font-size: 18px; font-weight: bold; color: #237091; }
.detail-header-actions { display: flex; align-items: center; gap: 10px; }
.btn-export-csv {
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #3986A8;
  background: white;
  border: 1px solid #3986A8;
  border-radius: 6px;
  cursor: pointer;
}
.btn-export-csv:hover { background: #e8f4f8; }

.detail-fields {
  display: flex;
  gap: 0;
  flex-direction: column;
  padding: 14px 18px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
}
.detail-field {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}
.detail-field + .detail-field { border-top: 1px solid #f3f4f6; }
.detail-field-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  width: 90px;
  flex-shrink: 0;
}
.detail-field-value {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #111;
}
.field-empty { color: #9ca3af; font-weight: 400; font-style: italic; }
.edit-icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  padding: 2px 4px;
  border-radius: 4px;
  opacity: 0.5;
  line-height: 1;
}
.edit-icon-btn:hover { opacity: 1; background: #f3f4f6; }
.detail-field-edit {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
}
.field-input {
  flex: 1;
  padding: 6px 10px;
  font-size: 14px;
  border: 1px solid #3986A8;
  border-radius: 6px;
  outline: none;
}
.save-btn {
  padding: 5px 10px;
  background: #3986A8;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
}
.save-btn:disabled { opacity: 0.4; cursor: default; }
.save-btn:hover:not(:disabled) { background: #237091; }
.cancel-btn {
  padding: 5px 10px;
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #6b7280;
}
.cancel-btn:hover { background: #f3f4f6; }

.administer-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 20px;
}
.administer-hint {
  font-size: 15px;
  color: #555;
  text-align: center;
  margin: 0;
}
.btn-start-admin {
  padding: 14px 36px;
  font-size: 16px;
  font-weight: 700;
  background: #3986A8;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  letter-spacing: 0.02em;
}
.btn-start-admin:hover { background: #237091; }


.tabs {
  display: flex;
  border-bottom: 1px solid #d1d5db;
  background: white;
}
.tab-btn {
  padding: 9px 18px;
  border: none;
  border-bottom: 3px solid transparent;
  background: none;
  font-size: 14px;
  color: #555;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;
}
.tab-btn:hover { color: #237091; }
.tab-btn.active { color: #237091; border-bottom-color: #3986A8; font-weight: 600; }

.tab-panel { padding: 16px 18px; }

.task-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.task-table th {
  text-align: left;
  padding: 6px 10px;
  border-bottom: 2px solid #d1d5db;
  color: #555;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.task-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e5e7eb;
  color: #333;
}
.task-table tr:last-child td { border-bottom: none; }

.task-status {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}
.task-status.scored  { background: #dcfce7; color: #166534; }
.task-status.pending { background: #f1f5f9; color: #475569; }

.none-chip { color: #aaa; font-size: 13px; }

.go-to-task-link {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #3986A8;
  background: transparent;
  border: 1px solid #3986A8;
  border-radius: 6px;
  cursor: pointer;
}
.go-to-task-link:hover { background: #e8f4f8; }


.empty-state { color: #aaa; font-size: 14px; text-align: center; padding: 24px 0; }
</style>
